import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert } from './UI';
import { useAuth } from '../context/AuthContext';
import { DELETION_CHANGED_EVENT, getDeletionStatus } from '../utils/accountDeletion';
import type { AccountDeletionStatus } from '../utils/accountDeletion';
import { formatDate } from '../utils/formatDate';

/** Erinnert angemeldete Nutzer mit laufendem Löschantrag daran und verlinkt zum Zurücknehmen. */
export const AccountDeletionBanner: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState<AccountDeletionStatus | null>(null);

  useEffect(() => {
    if (!isAuthenticated) { setStatus(null); return; }
    let cancelled = false;
    getDeletionStatus().then((s) => { if (!cancelled) setStatus(s); }).catch(() => {});
    return () => { cancelled = true; };
    // Bei jedem Seitenwechsel neu laden, damit das Banner nach Antrag/Rücknahme sofort stimmt.
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const onChanged = (e: Event) => setStatus((e as CustomEvent<AccountDeletionStatus>).detail);
    window.addEventListener(DELETION_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(DELETION_CHANGED_EVENT, onChanged);
  }, []);

  if (!status?.requested || !status.scheduledAt) return null;
  return (
    <div className="mb-4">
      <Alert
        type="warning"
        title={t('account:deletion.bannerTitle')}
        message={t('account:deletion.bannerMessage', { date: formatDate(status.scheduledAt) })}
      />
      {location.pathname !== '/account' && (
        <Link to="/account" className="mt-2 inline-block text-sm font-medium text-primary-300 hover:underline">
          {t('account:deletion.bannerLink')}
        </Link>
      )}
    </div>
  );
};
